import { 
  Card, 
  FormLayout, 
  TextField, 
  Select, 
  Checkbox, 
  RangeSlider, 
  Text, 
  BlockStack,
  Box
} from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { DisplaySettings, AISettings, DataCollectionSettings } from '../types';

interface SettingsFormProps {
  displaySettings: DisplaySettings;
  aiSettings: AISettings;
  dataCollectionSettings: DataCollectionSettings;
  onDisplaySettingsChange: (settings: DisplaySettings) => void;
  onAISettingsChange: (settings: AISettings) => void;
  onDataCollectionSettingsChange: (settings: DataCollectionSettings) => void;
}

export function SettingsForm({
  displaySettings,
  aiSettings,
  dataCollectionSettings,
  onDisplaySettingsChange,
  onAISettingsChange,
  onDataCollectionSettingsChange,
}: SettingsFormProps) {
  const { t } = useTranslation();

  const RECOMMENDATION_TYPE_OPTIONS = [
    { label: t('settings.recTypeSimilar'), value: 'similar' },
    { label: t('settings.recTypeTrending'), value: 'trending' },
    { label: t('settings.recTypePersonalized'), value: 'personalized' },
    { label: t('settings.recTypeFbt'), value: 'frequently-bought' },
    { label: t('settings.recTypeRecentlyViewed'), value: 'recently-viewed' },
  ];

  const CART_RECOMMENDATION_TYPE_OPTIONS = [
    { label: t('settings.recTypeSimilar'), value: 'similar' },
    { label: t('settings.recTypeTrending'), value: 'trending' },
    { label: t('settings.recTypePersonalized'), value: 'personalized' },
    { label: t('settings.recTypeFbt'), value: 'frequently-bought' },
  ];

  return (
    <BlockStack gap="500">
      {/* Display Settings */}
      <Card>
        <Box padding="400">
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">{t('settings.displaySettings')}</Text>
            <FormLayout>
              <Checkbox
                label={t('settings.showOnProductPage')}
                checked={displaySettings.showOnProductPage}
                onChange={(checked) =>
                  onDisplaySettingsChange({ ...displaySettings, showOnProductPage: checked })
                }
              />
              <Checkbox
                label={t('settings.showOnCartPage')}
                checked={displaySettings.showOnCartPage}
                onChange={(checked) =>
                  onDisplaySettingsChange({ ...displaySettings, showOnCartPage: checked })
                }
              />
              <Checkbox
                label={t('settings.showOnHomePage')}
                checked={displaySettings.showOnHomePage}
                onChange={(checked) =>
                  onDisplaySettingsChange({ ...displaySettings, showOnHomePage: checked })
                }
              />
              <TextField
                label={t('settings.widgetTitle')}
                value={displaySettings.widgetTitle}
                onChange={(value) =>
                  onDisplaySettingsChange({ ...displaySettings, widgetTitle: value })
                }
                autoComplete="off"
              />
              <RangeSlider
                label={`${t('settings.maxRecommendations')}: ${displaySettings.maxRecommendations}`}
                value={displaySettings.maxRecommendations}
                min={2}
                max={12}
                step={1}
                onChange={(value) =>
                  onDisplaySettingsChange({ 
                    ...displaySettings, 
                    maxRecommendations: typeof value === 'number' ? value : value[0] 
                  })
                }
                output
              />
              <Select
                label={t('settings.widgetStyle')}
                options={[
                  { label: t('settings.styleGrid'), value: 'grid' },
                  { label: t('settings.styleCarousel'), value: 'carousel' },
                  { label: t('settings.styleList'), value: 'list' },
                ]}
                value={displaySettings.widgetStyle}
                onChange={(value) =>
                  onDisplaySettingsChange({ 
                    ...displaySettings, 
                    widgetStyle: value as 'grid' | 'carousel' | 'list' 
                  })
                }
              />
              <Select
                label={t('settings.recommendationType')}
                options={RECOMMENDATION_TYPE_OPTIONS}
                value={displaySettings.recommendationType ?? 'similar'}
                onChange={(value) =>
                  onDisplaySettingsChange({ 
                    ...displaySettings, 
                    recommendationType: value as DisplaySettings['recommendationType']
                  })
                }
                helpText={t('settings.recTypeHelp')}
              />
            </FormLayout>
          </BlockStack>
        </Box>
      </Card>

      {/* Cart Widget Settings */}
      <Card>
        <Box padding="400">
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">{t('settings.cartWidget')}</Text>
            <Text as="p" tone="subdued">
              {t('settings.cartWidgetDesc')}
            </Text>
            <FormLayout>
              <Checkbox
                label={t('settings.enableCartWidget')}
                checked={displaySettings.cartWidgetEnabled ?? false}
                onChange={(checked) =>
                  onDisplaySettingsChange({ ...displaySettings, cartWidgetEnabled: checked })
                }
                helpText={t('settings.enableCartWidgetHelp')}
              />
              {displaySettings.cartWidgetEnabled && (
                <>
                  <TextField
                    label={t('settings.cartWidgetTitle')}
                    value={displaySettings.cartWidgetTitle ?? ''}
                    onChange={(value) =>
                      onDisplaySettingsChange({ ...displaySettings, cartWidgetTitle: value })
                    }
                    autoComplete="off"
                  />
                  <Select
                    label={t('settings.cartRecommendationType')}
                    options={CART_RECOMMENDATION_TYPE_OPTIONS}
                    value={displaySettings.cartWidgetType ?? 'frequently-bought'}
                    onChange={(value) =>
                      onDisplaySettingsChange({ 
                        ...displaySettings, 
                        cartWidgetType: value as DisplaySettings['cartWidgetType']
                      })
                    }
                    helpText={t('settings.cartRecTypeHelp')}
                  />
                </>
              )}
            </FormLayout>
          </BlockStack>
        </Box>
      </Card>

      {/* AI Settings */}
      <Card>
        <Box padding="400">
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">{t('settings.aiSettings')}</Text>
            <FormLayout>
              <Select
                label={t('settings.recommendationStrategy')}
                options={[
                  { label: t('settings.strategyHybrid'), value: 'hybrid' },
                  { label: t('settings.strategyCollaborative'), value: 'collaborative' },
                  { label: t('settings.strategyContentBased'), value: 'content-based' },
                ]}
                value={aiSettings.strategy}
                onChange={(value) =>
                  onAISettingsChange({ 
                    ...aiSettings, 
                    strategy: value as 'hybrid' | 'collaborative' | 'content-based' 
                  })
                }
                helpText={t('settings.strategyHelp')}
              />
              <RangeSlider
                label={`${t('settings.minConfidence')}: ${(aiSettings.minConfidence * 100).toFixed(0)}%`}
                value={aiSettings.minConfidence * 100}
                min={10}
                max={90}
                step={5}
                onChange={(value) =>
                  onAISettingsChange({ 
                    ...aiSettings, 
                    minConfidence: (typeof value === 'number' ? value : value[0]) / 100 
                  })
                }
                output
              />
              <Checkbox
                label={t('settings.enablePersonalization')}
                checked={aiSettings.enablePersonalization}
                onChange={(checked) =>
                  onAISettingsChange({ ...aiSettings, enablePersonalization: checked })
                }
                helpText={t('settings.enablePersonalizationHelp')}
              />
            </FormLayout>
          </BlockStack>
        </Box>
      </Card>

      {/* Data Collection Settings */}
      <Card>
        <Box padding="400">
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">{t('settings.dataCollection')}</Text>
            <Text as="p" tone="subdued">
              {t('settings.dataCollectionDesc')}
            </Text>
            <FormLayout>
              <Checkbox
                label={t('settings.trackProductViews')}
                checked={dataCollectionSettings.trackProductViews}
                onChange={(checked) =>
                  onDataCollectionSettingsChange({ ...dataCollectionSettings, trackProductViews: checked })
                }
              />
              <Checkbox
                label={t('settings.trackAddToCart')}
                checked={dataCollectionSettings.trackAddToCart}
                onChange={(checked) =>
                  onDataCollectionSettingsChange({ ...dataCollectionSettings, trackAddToCart: checked })
                }
              />
              <Checkbox
                label={t('settings.trackPurchases')}
                checked={dataCollectionSettings.trackPurchases}
                onChange={(checked) =>
                  onDataCollectionSettingsChange({ ...dataCollectionSettings, trackPurchases: checked })
                }
              />
              <Checkbox
                label={t('settings.trackSearches')}
                checked={dataCollectionSettings.trackSearches}
                onChange={(checked) =>
                  onDataCollectionSettingsChange({ ...dataCollectionSettings, trackSearches: checked })
                }
              />
            </FormLayout>
          </BlockStack>
        </Box>
      </Card>
    </BlockStack>
  );
}
